import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Settings2, Copy, Download, CheckCircle,
  CircleDot, Wrench, Calculator, ChevronDown, ChevronUp
} from 'lucide-react';

// InputField must be outside component to avoid remount on every render
const InputField = ({ label, value, onChange, step = 0.001, min, unit = '"', testId }) => (
  <div>
    <label className="block text-[11px] text-industrial-text-secondary mb-1">{label}</label>
    <div className="flex items-center gap-1">
      <input
        type="number"
        step={step}
        min={min}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text font-mono"
        data-testid={testId}
      />
      {unit && <span className="text-[10px] text-industrial-text-secondary">{unit}</span>}
    </div>
  </div>
);

const VPulleyPage = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);

  // Pulley geometry
  const [od, setOd] = useState(3.150);           // Outer diameter (inches)
  const [rootDia, setRootDia] = useState(1.970);  // Groove root diameter
  const [bottomWidth, setBottomWidth] = useState(0.315); // Groove bottom width
  const [vAngle, setVAngle] = useState(38);       // V included angle (degrees)
  const [firstGrooveZ, setFirstGrooveZ] = useState(-0.750); // First groove center Z
  const [grooveCount, setGrooveCount] = useState(2); // Number of grooves
  const [spacing, setSpacing] = useState(0.750);  // Center-to-center spacing
  const [stockLength, setStockLength] = useState(2.5);

  // Tool
  const [toolWidth, setToolWidth] = useState(0.125); // Grooving tool width
  const [cornerRadius, setCornerRadius] = useState(0.015); // Insert corner radius

  // Roughing
  const [radialPeck, setRadialPeck] = useState(0.050);
  const [radialClearance, setRadialClearance] = useState(0.020);
  const [roughZStep, setRoughZStep] = useState(0.040);
  const [finishStock, setFinishStock] = useState(0.005);

  // Feeds & RPM
  const [roughFeed, setRoughFeed] = useState(0.003); // IPR
  const [finishFeed, setFinishFeed] = useState(0.002);
  const [roughRpm, setRoughRpm] = useState(800);
  const [finishRpm, setFinishRpm] = useState(1000);

  // Part-off
  const [partOff, setPartOff] = useState(false);
  const [partOffZ, setPartOffZ] = useState(-2.25);
  const [partOffFinalX, setPartOffFinalX] = useState(0);
  const [partOffPeck, setPartOffPeck] = useState(0.050);
  const [partOffFeed, setPartOffFeed] = useState(0.002);
  const [partOffRpm, setPartOffRpm] = useState(600);

  // Sections
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Calculations
  const calc = useMemo(() => {
    const radialDepth = (od - rootDia) / 2;
    const halfAngle = vAngle / 2;
    const halfAngleRad = (halfAngle * Math.PI) / 180;
    const theoreticalTopWidth = bottomWidth + 2 * radialDepth * Math.tan(halfAngleRad);
    const grooveCenters = [];
    for (let i = 0; i < grooveCount; i++) {
      grooveCenters.push(firstGrooveZ - i * spacing);
    }
    const roughZLevels = roughZStep > 0 ? Math.ceil((theoreticalTopWidth / 2 - toolWidth / 2) / roughZStep) : 0;
    const radialPecks = radialPeck > 0 ? Math.ceil((radialDepth - finishStock) / radialPeck) : 0;
    const totalRoughPecks = radialPecks * (roughZLevels * 2 + 1) * grooveCount;
    const finishPasses = (roughZLevels * 2 + 1) * grooveCount;
    const rootFinishPasses = grooveCount;
    const partOffPecks = partOff && partOffPeck > 0 ? Math.ceil((od / 2 - partOffFinalX) / partOffPeck) : 0;
    const totalMoves = totalRoughPecks + finishPasses + rootFinishPasses + partOffPecks;

    return {
      radialDepth, halfAngle, halfAngleRad, theoreticalTopWidth,
      grooveCenters, roughZLevels, radialPecks, totalRoughPecks,
      finishPasses, rootFinishPasses, partOffPecks, totalMoves,
    };
  }, [od, rootDia, bottomWidth, vAngle, firstGrooveZ, grooveCount, spacing, toolWidth, roughZStep, radialPeck, finishStock, partOff, partOffFinalX, partOffPeck]);

  // Generate G-code
  const gcode = useMemo(() => {
    const lines = [];
    const c = calc;
    lines.push('%');
    lines.push('O5001 (V-PULLEY GROOVE PROGRAM)');
    lines.push(`(OD ${od.toFixed(3)} ROOT DIA ${rootDia.toFixed(3)})`);
    lines.push(`(V-ANGLE ${vAngle} DEG, ${grooveCount} GROOVE(S))`);
    lines.push(`(TOOL: ${toolWidth.toFixed(3)} WIDE, R${cornerRadius.toFixed(3)})`);
    lines.push('');
    lines.push('G18 G20 G40 G80 G99');
    lines.push('G50 S3500');
    lines.push('G28 U0 W0');
    lines.push('');
    lines.push('(GROOVING TOOL)');
    lines.push('T0808');
    lines.push(`G97 S${roughRpm} M03`);
    lines.push(`G00 X${(od + 0.2).toFixed(4)} Z${(firstGrooveZ + 1).toFixed(4)} M08`);

    // Guard against invalid inputs
    if (radialPeck <= 0 || calc.radialDepth <= 0) {
      lines.push('(ERROR: Invalid radial peck or depth - check inputs)');
      lines.push('M30');
      lines.push('%');
      return lines.join('\n');
    }

    // For each groove
    c.grooveCenters.forEach((gz, gi) => {
      lines.push('');
      lines.push(`(===== GROOVE ${gi + 1} AT Z${gz.toFixed(4)} =====)`);
      const leftEdge = gz - c.theoreticalTopWidth / 2;
      const rightEdge = gz + c.theoreticalTopWidth / 2;

      // Rough: center peck first, then step outward on both sides
      lines.push('(ROUGHING - PECK CYCLE)');
      const roughPositions = [gz]; // center first
      for (let s = 1; s <= c.roughZLevels; s++) {
        const offset = s * roughZStep;
        roughPositions.push(gz + offset);
        roughPositions.push(gz - offset);
      }

      roughPositions.forEach((zp) => {
        const targetX = rootDia + finishStock * 2;
        let currentX = od + 0.1;
        lines.push(`G00 Z${zp.toFixed(4)}`);
        lines.push(`G00 X${(od + 0.1).toFixed(4)}`);

        const safeRadPeck = Math.max(radialPeck, 0.005);
        // Peck down
        while (currentX > targetX) {
          const nextX = Math.max(currentX - safeRadPeck * 2, targetX);
          lines.push(`G01 X${nextX.toFixed(4)} F${roughFeed.toFixed(4)}`);
          lines.push(`G00 X${(currentX + radialClearance * 2).toFixed(4)}`);
          if (nextX > targetX) {
            lines.push(`G00 X${(nextX + 0.010).toFixed(4)}`);
          }
          currentX = nextX;
        }
        lines.push(`G00 X${(od + 0.1).toFixed(4)}`);
      });

      // Finish pass: left flank, root, right flank
      lines.push('');
      lines.push(`(FINISH GROOVE ${gi + 1})`);
      lines.push(`G97 S${finishRpm} M03`);

      // Right flank (positive Z side)
      const rootHalfWidth = bottomWidth / 2;
      lines.push(`G00 X${(od + 0.05).toFixed(4)} Z${(gz + c.theoreticalTopWidth / 2 + 0.020).toFixed(4)}`);
      lines.push(`G01 X${rootDia.toFixed(4)} Z${(gz + rootHalfWidth).toFixed(4)} F${finishFeed.toFixed(4)}`);
      lines.push(`G00 X${(od + 0.1).toFixed(4)}`);

      // Left flank (negative Z side)
      lines.push(`G00 Z${(gz - c.theoreticalTopWidth / 2 - 0.020).toFixed(4)}`);
      lines.push(`G01 X${rootDia.toFixed(4)} Z${(gz - rootHalfWidth).toFixed(4)} F${finishFeed.toFixed(4)}`);
      lines.push(`G00 X${(od + 0.1).toFixed(4)}`);

      // Root finish
      lines.push(`G00 Z${(gz - rootHalfWidth).toFixed(4)}`);
      lines.push(`G01 X${rootDia.toFixed(4)} F${finishFeed.toFixed(4)}`);
      lines.push(`G01 Z${(gz + rootHalfWidth).toFixed(4)}`);
      lines.push(`G00 X${(od + 0.1).toFixed(4)}`);

      lines.push(`G97 S${roughRpm} M03`);
    });

    // Part-off
    if (partOff) {
      lines.push('');
      lines.push('(===== PART OFF =====)');
      lines.push(`G97 S${partOffRpm} M03`);
      lines.push(`G00 X${(od + 0.1).toFixed(4)} Z${partOffZ.toFixed(4)}`);
      let poX = od + 0.1;
      const safePoPeck = Math.max(partOffPeck, 0.005);
      while (poX > partOffFinalX) {
        const nextX = Math.max(poX - safePoPeck * 2, partOffFinalX);
        lines.push(`G01 X${nextX.toFixed(4)} F${partOffFeed.toFixed(4)}`);
        if (nextX > partOffFinalX) {
          lines.push(`G00 X${(poX + radialClearance * 2).toFixed(4)}`);
          lines.push(`G00 X${(nextX + 0.010).toFixed(4)}`);
        }
        poX = nextX;
      }
      lines.push(`G00 X${(od + 0.5).toFixed(4)}`);
    }

    lines.push('');
    lines.push('M09');
    lines.push('G28 U0 W0');
    lines.push('M05');
    lines.push('M30');
    lines.push('%');
    return lines.join('\n');
  }, [calc, od, rootDia, bottomWidth, vAngle, firstGrooveZ, grooveCount, toolWidth, cornerRadius, roughFeed, finishFeed, roughRpm, finishRpm, radialPeck, radialClearance, finishStock, partOff, partOffZ, partOffFinalX, partOffPeck, partOffFeed, partOffRpm, roughZStep, spacing]);

  // 2D Profile Visualizer
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    // Scale: map part coords to canvas
    const margin = 40;
    const partMaxX = od / 2 + 0.2;
    const partMinZ = firstGrooveZ - (grooveCount - 1) * spacing - calc.theoreticalTopWidth;
    const partMaxZ = 0.2;
    const zRange = Math.abs(partMaxZ - partMinZ) || 1;
    const xRange = partMaxX || 1;
    const scaleZ = (W - margin * 2) / zRange;
    const scaleX = (H - margin * 2) / xRange;
    const scale = Math.min(scaleZ, scaleX);

    const mapZ = (z) => margin + (partMaxZ - z) * scale;
    const mapX = (x) => H - margin - x * scale;

    // Background
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, W, H);

    // Stock outline
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(mapZ(0), mapX(0));
    ctx.lineTo(mapZ(0), mapX(od / 2));
    ctx.lineTo(mapZ(-stockLength), mapX(od / 2));
    ctx.lineTo(mapZ(-stockLength), mapX(0));
    ctx.stroke();
    ctx.setLineDash([]);

    // Part profile (OD line)
    ctx.strokeStyle = '#60a5fa';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(mapZ(0), mapX(od / 2));

    // Draw V-grooves
    calc.grooveCenters.forEach((gz) => {
      const topHalf = calc.theoreticalTopWidth / 2;
      const botHalf = bottomWidth / 2;
      const rDepth = calc.radialDepth;
      // Approach from OD to groove start
      ctx.lineTo(mapZ(gz + topHalf), mapX(od / 2));
      // Right flank down
      ctx.lineTo(mapZ(gz + botHalf), mapX(rootDia / 2));
      // Root
      ctx.lineTo(mapZ(gz - botHalf), mapX(rootDia / 2));
      // Left flank up
      ctx.lineTo(mapZ(gz - topHalf), mapX(od / 2));
    });

    // Continue to end
    ctx.lineTo(mapZ(-stockLength), mapX(od / 2));
    ctx.stroke();

    // Centerline
    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 1;
    ctx.setLineDash([8, 4]);
    ctx.beginPath();
    ctx.moveTo(mapZ(0.5), mapX(0));
    ctx.lineTo(mapZ(-stockLength - 0.5), mapX(0));
    ctx.stroke();
    ctx.setLineDash([]);

    // Root diameter line
    ctx.strokeStyle = '#f59e0b44';
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 4]);
    ctx.beginPath();
    ctx.moveTo(mapZ(0.2), mapX(rootDia / 2));
    ctx.lineTo(mapZ(-stockLength - 0.2), mapX(rootDia / 2));
    ctx.stroke();
    ctx.setLineDash([]);

    // Labels
    ctx.fillStyle = '#94a3b8';
    ctx.font = '11px monospace';
    ctx.fillText(`OD ${od.toFixed(3)}"`, mapZ(0) + 4, mapX(od / 2) - 4);
    ctx.fillText(`ROOT ${rootDia.toFixed(3)}"`, mapZ(0) + 4, mapX(rootDia / 2) - 4);

    // Groove center markers
    ctx.fillStyle = '#f59e0b';
    calc.grooveCenters.forEach((gz, i) => {
      ctx.beginPath();
      ctx.arc(mapZ(gz), mapX(rootDia / 2), 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#e2e8f0';
      ctx.font = '10px monospace';
      ctx.fillText(`G${i + 1} Z${gz.toFixed(3)}`, mapZ(gz) - 20, mapX(rootDia / 2) + 14);
      ctx.fillStyle = '#f59e0b';
    });

    // Part-off line
    if (partOff) {
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(mapZ(partOffZ), mapX(0));
      ctx.lineTo(mapZ(partOffZ), mapX(od / 2 + 0.1));
      ctx.stroke();
      ctx.fillStyle = '#ef4444';
      ctx.font = '10px monospace';
      ctx.fillText('PART OFF', mapZ(partOffZ) - 20, mapX(od / 2 + 0.1) - 6);
    }
  }, [calc, od, rootDia, bottomWidth, firstGrooveZ, grooveCount, spacing, stockLength, partOff, partOffZ]);

  const handleCopy = () => {
    navigator.clipboard.writeText(gcode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([gcode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'v-pulley-groove.nc';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-industrial-bg pb-20" data-testid="v-pulley-page">
      {/* Header */}
      <div className="sticky top-14 z-20 bg-industrial-surface border-b border-industrial-border px-4 py-3">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-1 text-industrial-text-secondary hover:text-industrial-text" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" /><span className="text-sm">Back</span>
          </button>
          <h2 className="font-heading font-bold text-industrial-safety flex items-center gap-2">
            <CircleDot className="w-5 h-5" /> V-PULLEY GROOVE
          </h2>
          <div className="w-12" />
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* 2D Profile Preview */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-industrial-border flex items-center justify-between">
            <span className="text-xs font-medium text-industrial-text">V-Pulley Profile</span>
            <span className="text-[10px] text-industrial-text-secondary">
              {grooveCount} groove{grooveCount > 1 ? 's' : ''} | {vAngle}° V-angle | OD {od}" / Root {rootDia}"
            </span>
          </div>
          <canvas ref={canvasRef} width={800} height={300} className="w-full bg-[#1a1a2e]" data-testid="profile-canvas" />
        </div>

        {/* Pulley Geometry */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <h3 className="text-sm font-medium text-industrial-text mb-3 flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-industrial-safety" /> Pulley Geometry
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <InputField label="Outer Diameter" value={od} onChange={setOd} testId="od-input" />
            <InputField label="Root Diameter" value={rootDia} onChange={setRootDia} testId="root-dia-input" />
            <InputField label="Bottom Width" value={bottomWidth} onChange={setBottomWidth} testId="bottom-width-input" />
            <InputField label="V Included Angle" value={vAngle} onChange={setVAngle} step={0.1} unit="°" testId="v-angle-input" />
            <InputField label="First Groove Z" value={firstGrooveZ} onChange={setFirstGrooveZ} testId="first-groove-z" />
            <InputField label="Groove Count" value={grooveCount} onChange={(v) => setGrooveCount(Math.max(1, Math.round(v)))} step={1} min={1} unit="" testId="groove-count" />
            <InputField label="Center Spacing" value={spacing} onChange={setSpacing} testId="spacing-input" />
            <InputField label="Stock Length" value={stockLength} onChange={setStockLength} testId="stock-length" />
          </div>
        </div>

        {/* Tool & Cutting */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <h3 className="text-sm font-medium text-industrial-text mb-3 flex items-center gap-2">
            <Wrench className="w-4 h-4 text-industrial-safety" /> Tool & Cutting Parameters
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <InputField label="Tool Width" value={toolWidth} onChange={setToolWidth} testId="tool-width" />
            <InputField label="Corner Radius" value={cornerRadius} onChange={setCornerRadius} testId="corner-radius" />
            <InputField label="Radial Peck" value={radialPeck} onChange={setRadialPeck} testId="radial-peck" />
            <InputField label="Finish Stock" value={finishStock} onChange={setFinishStock} testId="finish-stock" />
            <InputField label="Rough Feed (IPR)" value={roughFeed} onChange={setRoughFeed} unit="IPR" testId="rough-feed" />
            <InputField label="Finish Feed (IPR)" value={finishFeed} onChange={setFinishFeed} unit="IPR" testId="finish-feed" />
            <InputField label="Rough RPM" value={roughRpm} onChange={(v) => setRoughRpm(Math.round(v))} step={50} unit="RPM" testId="rough-rpm" />
            <InputField label="Finish RPM" value={finishRpm} onChange={(v) => setFinishRpm(Math.round(v))} step={50} unit="RPM" testId="finish-rpm" />
          </div>
        </div>

        {/* Advanced + Part-Off */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300"
        >
          {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          Advanced & Part-Off
        </button>
        {showAdvanced && (
          <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <InputField label="Radial Clearance" value={radialClearance} onChange={setRadialClearance} testId="radial-clearance" />
              <InputField label="Rough Z Step" value={roughZStep} onChange={setRoughZStep} testId="rough-z-step" />
            </div>
            <div className="border-t border-industrial-border pt-3">
              <div className="flex items-center gap-3 mb-3">
                <h4 className="text-sm font-medium text-industrial-text">Part-Off</h4>
                <button
                  onClick={() => setPartOff(!partOff)}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${partOff ? 'bg-industrial-safety/20 border-industrial-safety text-industrial-safety' : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary'}`}
                  data-testid="part-off-toggle"
                >
                  {partOff ? 'ON' : 'OFF'}
                </button>
              </div>
              {partOff && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <InputField label="Part-Off Z" value={partOffZ} onChange={setPartOffZ} testId="part-off-z" />
                  <InputField label="Final X" value={partOffFinalX} onChange={setPartOffFinalX} testId="part-off-x" />
                  <InputField label="Peck Depth" value={partOffPeck} onChange={setPartOffPeck} testId="part-off-peck" />
                  <InputField label="Feed (IPR)" value={partOffFeed} onChange={setPartOffFeed} unit="IPR" testId="part-off-feed" />
                  <InputField label="RPM" value={partOffRpm} onChange={(v) => setPartOffRpm(Math.round(v))} step={50} unit="RPM" testId="part-off-rpm" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Calculated Info */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <h3 className="text-sm font-medium text-industrial-text mb-3 flex items-center gap-2">
            <Calculator className="w-4 h-4 text-industrial-safety" /> Calculated Information
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
            <div className="flex justify-between"><span className="text-industrial-text-secondary">Radial Depth</span><span className="font-mono text-industrial-text">{calc.radialDepth.toFixed(4)}"</span></div>
            <div className="flex justify-between"><span className="text-industrial-text-secondary">Half V Angle</span><span className="font-mono text-industrial-text">{calc.halfAngle.toFixed(1)}°</span></div>
            <div className="flex justify-between"><span className="text-industrial-text-secondary">Top Width</span><span className="font-mono text-industrial-text">{calc.theoreticalTopWidth.toFixed(4)}"</span></div>
            <div className="flex justify-between"><span className="text-industrial-text-secondary">Tool Width</span><span className="font-mono text-industrial-text">{toolWidth.toFixed(3)}"</span></div>
            <div className="flex justify-between"><span className="text-industrial-text-secondary">Corner Radius</span><span className="font-mono text-industrial-text">R{cornerRadius.toFixed(3)}"</span></div>
            <div className="flex justify-between"><span className="text-industrial-text-secondary">Grooves</span><span className="font-mono text-industrial-text">{grooveCount}</span></div>
            <div className="flex justify-between"><span className="text-industrial-text-secondary">Groove Centers</span><span className="font-mono text-industrial-text text-xs">{calc.grooveCenters.map(z => `Z${z.toFixed(3)}`).join(' / ')}</span></div>
            <div className="flex justify-between"><span className="text-industrial-text-secondary">Rough Z Levels</span><span className="font-mono text-industrial-text">{calc.roughZLevels}</span></div>
            <div className="flex justify-between"><span className="text-industrial-text-secondary">Total Rough Pecks</span><span className="font-mono text-industrial-text">{calc.totalRoughPecks}</span></div>
            <div className="flex justify-between"><span className="text-industrial-text-secondary">Finish Passes</span><span className="font-mono text-industrial-text">{calc.finishPasses}</span></div>
            {partOff && <div className="flex justify-between"><span className="text-industrial-text-secondary">Part-Off Pecks</span><span className="font-mono text-industrial-text">{calc.partOffPecks}</span></div>}
            <div className="flex justify-between"><span className="text-industrial-text-secondary text-industrial-safety font-medium">Total Moves</span><span className="font-mono text-industrial-safety font-bold">{calc.totalMoves}</span></div>
          </div>
        </div>

        {/* G-Code Output */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
          <div className="px-4 py-2 border-b border-industrial-border flex items-center justify-between">
            <span className="text-sm font-medium text-industrial-text">Generated G-Code</span>
            <div className="flex gap-2">
              <button onClick={handleCopy} className="px-3 py-1 text-xs bg-industrial-surface-highlight rounded flex items-center gap-1 text-industrial-text-secondary hover:text-industrial-text" data-testid="copy-gcode">
                {copied ? <><CheckCircle className="w-3 h-3 text-green-400" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
              <button onClick={handleDownload} className="px-3 py-1 text-xs bg-industrial-safety/20 rounded flex items-center gap-1 text-industrial-safety" data-testid="download-gcode">
                <Download className="w-3 h-3" /> .NC
              </button>
            </div>
          </div>
          <pre className="p-4 text-xs font-mono text-industrial-text overflow-x-auto max-h-96 overflow-y-auto leading-relaxed" data-testid="gcode-output">
            {gcode}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default VPulleyPage;
